// SPDX-License-Identifier: GPL-3.0-or-later
import type {
  PdfjsViewer,
  PdfjsViewerPageContentDetail,
  PdfjsViewerPageSource,
  PdfjsViewerPageSourceOverride,
} from "@unilarva/pdfjs-viewer";
import type { MultikeyOccurrence, MultikeySelectionBackend } from "./multikey";
import type { MaterializationRequest, MaterializedPages } from "./multikey-materializer";
import type { MultikeyIndex } from "./multikey-index";
import { multikeyRangeForPage } from "./multikey-index";
import { MultikeyArtifactCache, type CachedArtifactPage } from "./multikey-artifact-cache";
import {
  MULTIKEY_CACHE_BYTES,
  MULTIKEY_MATERIALIZER_PROFILE,
  MULTIKEY_MAX_JOB_PAGES,
} from "./multikey-worker-protocol";

/** Injectable worker boundary; neither this module nor the RPC client imports the PDF engine. */
export interface MultikeyBackendClient {
  convert(
    request: MaterializationRequest,
    options: { signal?: AbortSignal },
  ): Promise<MaterializedPages>;
  dispose(): void;
  readonly diagnostics: {
    readonly sourceBytes: number;
    readonly workerStarts: number;
    readonly conversions: number;
  };
}
export interface MultikeyBackendOptions {
  readonly viewer: Pick<
    PdfjsViewer,
    "documentId" | "preparePageSource" | "setPageSourceOverrides" | "state"
  >;
  readonly rootEventTarget: EventTarget;
  readonly client: MultikeyBackendClient;
  readonly index: MultikeyIndex;
  readonly indexIdentity: string;
  readonly occurrences: readonly MultikeyOccurrence[];
  readonly signal: AbortSignal;
  readonly visiblePages?: () => readonly number[];
  /** Disk cache is optional and never owns musical state or admitted viewer readers. */
  readonly artifacts?: Pick<MultikeyArtifactCache, "lookup" | "put" | "close">;
  readonly cacheBytes?: number;
}
type PageStatus = {
  revision: number | null;
  sourceId: string | null;
  status: "pending" | "ready" | "error";
};
type Applied = Awaited<ReturnType<MultikeySelectionBackend["apply"]>>;
const abortError = (): Error => new DOMException("Multikey work cancelled", "AbortError");

/** Transactional page-source adapter. The controller is the only permanent selection authority. */
export class MultikeyBackend implements MultikeySelectionBackend {
  private readonly documentId: number;
  private readonly byPage = new Map<number, MultikeyOccurrence[]>();
  private readonly installed = new Map<number, PdfjsViewerPageSourceOverride>();
  private readonly handles = new Set<PdfjsViewerPageSource>();
  private readonly pageStatus = new Map<number, PageStatus>();
  private readonly listeners = new Set<() => void>();
  private readonly wake = new Set<() => void>();
  private readonly cache = new Map<string, CachedArtifactPage>();
  private readonly artifacts: NonNullable<MultikeyBackendOptions["artifacts"]>;
  private cacheSize = 0;
  private tail: Promise<unknown> = Promise.resolve();
  private disposed = false;
  private processing = new Set<number>();
  private earlyEvents: PdfjsViewerPageContentDetail[] | undefined;
  private hits = 0;
  private conversions = 0;
  private cacheWriteFailures = 0;
  private pixelFailures = 0;
  private rollbackFailures = 0;
  private readonly abort = (): void => this.dispose();
  private readonly documentChange = (): void => {
    if (this.options.viewer.documentId !== this.documentId) this.dispose();
    else this.notify();
  };
  private readonly content = (event: Event): void => {
    const detail = (event as CustomEvent<PdfjsViewerPageContentDetail>).detail;
    if (
      !detail ||
      this.disposed ||
      detail.documentId !== this.documentId ||
      this.options.viewer.documentId !== this.documentId
    )
      return;
    if (this.earlyEvents) {
      // Only the most recent event for each exact slot revision/source can matter.
      const index = this.earlyEvents.findIndex(
        e =>
          e.page === detail.page &&
          e.revision === detail.revision &&
          e.sourceId === detail.sourceId,
      );
      if (index >= 0) this.earlyEvents[index] = detail;
      else if (this.earlyEvents.length < 512) this.earlyEvents.push(detail);
    }
    this.accept(detail);
  };

  /** Takes ownership of client lifetime and captures one exact numeric viewer document token. */
  public constructor(private readonly options: MultikeyBackendOptions) {
    const id = options.viewer.documentId;
    if (id === null || !Number.isSafeInteger(id) || options.signal.aborted) throw abortError();
    if (options.index.pageCount !== options.viewer.state.pageCount)
      throw new Error("Multikey index page count mismatch");
    if (
      options.cacheBytes !== undefined &&
      (!Number.isSafeInteger(options.cacheBytes) ||
        options.cacheBytes < 0 ||
        options.cacheBytes > MULTIKEY_CACHE_BYTES)
    )
      throw new Error("Invalid multikey cache budget");
    this.documentId = id;
    this.artifacts = options.artifacts ?? new MultikeyArtifactCache();
    for (const occurrence of options.occurrences)
      for (const index of occurrence.layerPageIndices) {
        const page = index + 1;
        const entries = this.byPage.get(page) ?? [];
        entries.push(occurrence);
        this.byPage.set(page, entries);
      }
    options.rootEventTarget.addEventListener("pdf:pagecontent", this.content);
    options.rootEventTarget.addEventListener("pdf:statechange", this.documentChange);
    options.signal.addEventListener("abort", this.abort, { once: true });
  }

  /** Byte/call counters represent only resources actually owned or jobs actually submitted here. */
  public get diagnostics(): {
    conversions: number;
    cacheHits: number;
    derivedBytes: number;
    sourceBytes: number;
    workerStarts: number;
    cacheWriteFailures: number;
    pixelFailures: number;
    rollbackFailures: number;
  } {
    return {
      conversions: this.conversions,
      cacheHits: this.hits,
      derivedBytes: this.cacheSize,
      sourceBytes: this.options.client.diagnostics.sourceBytes,
      workerStarts: this.options.client.diagnostics.workerStarts,
      cacheWriteFailures: this.cacheWriteFailures,
      pixelFailures: this.pixelFailures,
      rollbackFailures: this.rollbackFailures,
    };
  }

  private active(signal?: AbortSignal): boolean {
    return (
      !this.disposed &&
      !this.options.signal.aborted &&
      !signal?.aborted &&
      this.options.viewer.documentId === this.documentId
    );
  }
  private check(signal: AbortSignal): void {
    if (!this.active(signal)) throw abortError();
  }
  private notify(): void {
    for (const listener of this.listeners) {
      try {
        listener();
      } catch {
        /* Observer failures cannot revoke a mapping. */
      }
    }
    for (const listener of this.wake) listener();
  }
  private accept(detail: PdfjsViewerPageContentDetail): void {
    const state = this.pageStatus.get(detail.page);
    if (!state || state.revision !== detail.revision || state.sourceId !== detail.sourceId) return;
    state.status = detail.status;
    this.notify();
  }
  private pagesFor(occurrence?: MultikeyOccurrence): readonly number[] {
    if (!occurrence) return this.visiblePages();
    return occurrence.pageIndex === null
      ? occurrence.layerPageIndices.map(p => p + 1)
      : [occurrence.pageIndex + 1];
  }
  /** Anchor readiness for positioned controls; global status follows visible slots, not lazy offscreen work. */
  public pending(occurrence?: MultikeyOccurrence): boolean {
    return (
      (occurrence === undefined && this.processing.size > 0) ||
      this.pagesFor(occurrence).some(
        p => this.processing.has(p) || this.pageStatus.get(p)?.status === "pending",
      )
    );
  }
  /** Localized current-pixel failures; unrelated occurrences are not marked failed. */
  public failed(occurrence?: MultikeyOccurrence): boolean {
    return this.pagesFor(occurrence).some(p => this.pageStatus.get(p)?.status === "error");
  }
  /** Observes display pending/failure transitions; listener exceptions are isolated. */
  public subscribe(listener: () => void): () => void {
    if (!this.disposed) this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private pageKey(page: number, target: ReadonlyMap<MultikeyOccurrence, number>): string {
    const range = multikeyRangeForPage(this.options.index, page);
    return JSON.stringify([
      this.options.indexIdentity,
      this.options.index.canonical.identity,
      range.identity,
      MULTIKEY_MATERIALIZER_PROFILE,
      page,
      this.options.index.geometry[page - 1],
      this.byPage
        .get(page)!
        .map(o => [o.layerPrefix, target.get(o)!])
        .sort(),
    ]);
  }
  private remember(entries: ReadonlyMap<string, CachedArtifactPage>): void {
    const limit = this.options.cacheBytes ?? 8 * 1024 * 1024;
    for (const [key, entry] of entries) {
      this.cache.delete(key);
      if (entry.blob.size <= limit) this.cache.set(key, entry);
    }
    // A shared bundle is charged once, regardless of its number of page-index entries.
    const total = (): number =>
      [...new Map([...this.cache.values()].map(v => [v.id, v.blob.size])).values()].reduce(
        (a, b) => a + b,
        0,
      );
    while (total() > limit && this.cache.size) {
      const id = this.cache.values().next().value!.id;
      for (const [key, value] of this.cache) if (value.id === id) this.cache.delete(key);
    }
    this.cacheSize = total();
  }
  private pruneHandles(): void {
    const used = new Set([...this.installed.values()].map(v => v.source));
    for (const handle of this.handles)
      if (!used.has(handle)) {
        this.handles.delete(handle);
        try {
          handle.release();
        } catch {
          /* Release is idempotent in the public API. */
        }
      }
  }
  private failure(error: unknown, signal: AbortSignal): Applied {
    const reason = (error as { reason?: string })?.reason;
    return !this.active(signal) ||
      (error as Error)?.name === "AbortError" ||
      ["cancelled", "stale-document", "superseded", "destroyed"].includes(reason ?? "")
      ? { ok: false, reason: "cancelled" }
      : { ok: false, reason: "error", error };
  }
  private publish(
    changes: readonly PdfjsViewerPageSourceOverride[],
    revision: number,
    changed: readonly number[],
  ): void {
    const changedSet = new Set(changed);
    for (const change of changes) {
      if (change.source === null) this.installed.delete(change.page);
      else this.installed.set(change.page, change);
      if (changedSet.has(change.page))
        this.pageStatus.set(change.page, {
          revision,
          sourceId: change.source?.id ?? null,
          status: "pending",
        });
    }
    const events = this.earlyEvents;
    this.earlyEvents = undefined;
    for (const event of events ?? []) this.accept(event);
    this.notify();
  }
  private visiblePages(): readonly number[] {
    const state = this.options.viewer.state;
    const first =
      state.effectivePageLayout === "double"
        ? state.currentPage - ((state.currentPage - 1) % 2)
        : state.effectivePageLayout === "book" && state.currentPage > 1
          ? state.currentPage - (state.currentPage % 2)
          : state.currentPage;
    const fallback =
      state.effectivePageLayout === "single" ||
      !state.effectivePageLayout ||
      (state.effectivePageLayout === "book" && first === 1)
        ? [first]
        : [first, first + 1].filter(p => p <= state.pageCount);
    return this.options.visiblePages?.() ?? fallback;
  }
  private async visibleReady(pages: readonly number[], signal: AbortSignal): Promise<boolean> {
    const wanted = (): readonly number[] => {
      const visible = new Set(this.visiblePages());
      return pages.filter(p => visible.has(p));
    };
    const finished = (): boolean =>
      !this.active(signal) ||
      wanted().some(p => this.pageStatus.get(p)?.status === "error") ||
      wanted().every(p => this.pageStatus.get(p)?.status !== "pending");
    let wake = (): void => {};
    try {
      if (!finished())
        await new Promise<void>(resolve => {
          wake = (): void => {
            if (finished()) resolve();
          };
          this.wake.add(wake);
          signal.addEventListener("abort", wake, { once: true });
          wake();
        });
    } finally {
      this.wake.delete(wake);
      signal.removeEventListener("abort", wake);
    }
    return !wanted().some(p => this.pageStatus.get(p)?.status === "error");
  }

  /** Complete controller vectors are snapshotted for each serialized transaction, never retained as state. */
  public apply(
    current: ReadonlyMap<MultikeyOccurrence, number>,
    target: ReadonlyMap<MultikeyOccurrence, number>,
    options: { signal: AbortSignal },
  ): Promise<Applied> {
    const before = new Map(current),
      after = new Map(target);
    const signal = options.signal;
    const job = this.tail.then(() => this.applyNow(before, after, signal));
    this.tail = job.catch(() => {});
    return job;
  }

  private async applyNow(
    current: ReadonlyMap<MultikeyOccurrence, number>,
    target: ReadonlyMap<MultikeyOccurrence, number>,
    signal: AbortSignal,
  ): Promise<Applied> {
    let committed = false;
    const previousStatus = new Map(this.pageStatus);
    let affected: number[] = [];
    try {
      this.check(signal);
      if (
        current.size !== this.options.occurrences.length ||
        target.size !== this.options.occurrences.length ||
        this.options.occurrences.some(
          o =>
            !Number.isInteger(current.get(o)) ||
            !Number.isInteger(target.get(o)) ||
            current.get(o)! < 0 ||
            current.get(o)! > 11 ||
            target.get(o)! < 0 ||
            target.get(o)! > 11,
        )
      )
        throw new Error("Incomplete multikey selection vector");
      affected = [
        ...new Set(
          this.options.occurrences
            .filter(o => current.get(o) !== target.get(o))
            .flatMap(o => o.layerPageIndices.map(p => p + 1)),
        ),
      ].sort((a, b) => a - b);
      if (!affected.length) return { ok: true };
      this.processing = new Set(affected);
      this.notify();
      const previous = affected.map(
        page => this.installed.get(page) ?? ({ page, source: null } as const),
      );
      const changes: PdfjsViewerPageSourceOverride[] = [];
      const nondefault = affected.filter(page => {
        if (this.byPage.get(page)!.every(o => target.get(o) === 0)) {
          changes.push({ page, source: null });
          return false;
        }
        return true;
      });
      const keys = new Map(nondefault.map(page => [page, this.pageKey(page, target)]));
      const cached = new Map<string, CachedArtifactPage>();
      for (const key of keys.values()) {
        const entry = this.cache.get(key);
        if (entry) cached.set(key, entry);
      }
      try {
        const missing = [...keys.values()].filter(key => !cached.has(key));
        for (const [key, entry] of await this.artifacts.lookup(missing)) cached.set(key, entry);
      } catch {
        /* Disk denial and eviction are cache misses, not selection failures. */
      }
      this.remember(cached);
      const preparedById = new Map<string, { source: PdfjsViewerPageSource; pageCount: number }>();
      const prepare = async (
        id: string,
        blob: Blob,
        pageCount: number,
      ): Promise<PdfjsViewerPageSource> => {
        const existing = preparedById.get(id);
        if (existing) return existing.source;
        this.check(signal);
        const prepared = await this.options.viewer.preparePageSource({ blob }, this.documentId, {
          signal,
          linkContext: "base",
        });
        if (!prepared.ok) throw prepared;
        this.handles.add(prepared.source);
        this.check(signal);
        if (prepared.source.pageCount !== pageCount)
          throw new Error("Prepared multikey page count mismatch");
        preparedById.set(id, { source: prepared.source, pageCount });
        return prepared.source;
      };
      const missing: number[] = [];
      for (const page of nondefault) {
        const entry = cached.get(keys.get(page)!);
        if (!entry) {
          missing.push(page);
          continue;
        }
        try {
          if (
            !(entry.blob instanceof Blob) ||
            !entry.blob.size ||
            entry.blob.size > MULTIKEY_CACHE_BYTES ||
            !Number.isSafeInteger(entry.pageCount) ||
            entry.pageCount < 1 ||
            entry.pageCount > MULTIKEY_MAX_JOB_PAGES ||
            !Number.isSafeInteger(entry.sourcePage) ||
            entry.sourcePage < 1 ||
            entry.sourcePage > entry.pageCount
          )
            throw new Error("Corrupt cached multikey page metadata");
          const source = await prepare(entry.id, entry.blob, entry.pageCount);
          changes.push({ page, source, sourcePage: entry.sourcePage });
          this.hits++;
        } catch (error) {
          this.check(signal);
          const reason = (error as { reason?: string })?.reason;
          if (["cancelled", "stale-document", "superseded", "destroyed"].includes(reason ?? ""))
            throw error;
          // Persistent bytes are an optimization. A rejected cached PDF is regenerated from sources.
          for (const [key, value] of cached)
            if (value.id === entry.id) {
              cached.delete(key);
              this.cache.delete(key);
            }
          this.remember(new Map());
          missing.push(page);
        }
      }
      // Never combine local page numbers from distinct source ranges in one processor job.
      for (let start = 0; start < missing.length;) {
        this.check(signal);
        const range = multikeyRangeForPage(this.options.index, missing[start]!);
        const pages: number[] = [];
        while (
          start < missing.length &&
          pages.length < MULTIKEY_MAX_JOB_PAGES &&
          multikeyRangeForPage(this.options.index, missing[start]!) === range
        )
          pages.push(missing[start++]!);
        const relevant = [...new Set(pages.flatMap(p => this.byPage.get(p)!))].sort((a, b) =>
          a.layerPrefix.localeCompare(b.layerPrefix),
        );
        const selections = relevant.map(o => ({
          layerPrefix: o.layerPrefix,
          offset: target.get(o)!,
        }));
        this.conversions++;
        const result = await this.options.client.convert({ pages, selections }, { signal });
        this.check(signal);
        this.validateResult(result, pages);
        const blob = new Blob([result.data as Uint8Array<ArrayBuffer>], {
          type: "application/pdf",
        });
        const id = JSON.stringify(pages.map(p => keys.get(p)));
        const source = await prepare(id, blob, pages.length);
        this.check(signal);
        const entries = new Map(
          result.pages.map(p => [
            keys.get(p.logicalPage)!,
            {
              id,
              blob,
              pageCount: pages.length,
              sourcePage: p.sourcePage,
            },
          ]),
        );
        this.remember(entries);
        try {
          await this.artifacts.put({
            blob,
            pageCount: pages.length,
            pages: result.pages.map(p => ({ ...p, key: keys.get(p.logicalPage)! })),
          });
        } catch {
          this.cacheWriteFailures++;
        }
        this.check(signal);
        for (const page of result.pages)
          changes.push({
            page: page.logicalPage,
            source,
            sourcePage: page.sourcePage,
          });
      }
      this.check(signal);
      this.earlyEvents = [];
      const mapped = await this.options.viewer.setPageSourceOverrides(changes, this.documentId, {
        signal,
      });
      if (!mapped.ok) throw mapped;
      // Mapping publication is authoritative even if abort arrived while its promise settled.
      committed = true;
      this.publish(changes, mapped.revision, mapped.pages);
      this.processing.clear();
      this.notify();
      if (!this.active(signal)) return { ok: true };
      const ready = await this.visibleReady(mapped.pages, signal);
      if (!ready && this.active(signal)) {
        this.pixelFailures++;
        this.earlyEvents = [];
        try {
          const rollback = await this.options.viewer.setPageSourceOverrides(
            previous,
            this.documentId,
            { signal },
          );
          if (rollback.ok) {
            this.publish(previous, rollback.revision, rollback.pages);
            committed = false;
            return {
              ok: false,
              reason: "error",
              error: new Error("Visible multikey pixels failed; previous mapping restored"),
            };
          }
          this.rollbackFailures++;
        } catch {
          this.rollbackFailures++;
        }
        this.earlyEvents = undefined;
      }
      return { ok: true };
    } catch (error) {
      if (committed) return { ok: true };
      for (const page of affected) {
        const status = previousStatus.get(page);
        if (status) this.pageStatus.set(page, status);
        else this.pageStatus.delete(page);
      }
      return this.failure(error, signal);
    } finally {
      this.earlyEvents = undefined;
      this.processing.clear();
      if (this.disposed) this.installed.clear();
      this.pruneHandles();
      this.notify();
    }
  }

  private validateResult(result: MaterializedPages, pages: readonly number[]): void {
    if (
      !(result.data instanceof Uint8Array) ||
      !result.data.byteLength ||
      result.data.byteLength > MULTIKEY_CACHE_BYTES ||
      result.linkContext !== "canonical-base" ||
      result.pages.length !== pages.length ||
      new Set(result.pages.map(p => p.logicalPage)).size !== pages.length ||
      new Set(result.pages.map(p => p.sourcePage)).size !== pages.length ||
      result.pages.some(
        p =>
          !pages.includes(p.logicalPage) ||
          !Number.isInteger(p.sourcePage) ||
          p.sourcePage < 1 ||
          p.sourcePage > pages.length,
      )
    )
      throw new Error("Invalid multikey conversion page mapping");
  }

  /** Idempotent logical release; the viewer retains its own installed/physical source leases. */
  public dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.options.rootEventTarget.removeEventListener("pdf:pagecontent", this.content);
    this.options.rootEventTarget.removeEventListener("pdf:statechange", this.documentChange);
    this.options.signal.removeEventListener("abort", this.abort);
    this.options.client.dispose();
    this.installed.clear();
    this.pruneHandles();
    this.cache.clear();
    this.artifacts.close();
    this.cacheSize = 0;
    this.processing.clear();
    this.pageStatus.clear();
    this.notify();
    this.listeners.clear();
    this.wake.clear();
  }
}
